import React from "react";

// THIRD PARTY
import classNames from "classnames";
import PropTypes from "prop-types";

// PATTERN LIBRARY
import { Checkbox, SizeTile, ColorSwatch } from "@lululemon/ui-library";

// UTILS AND ANALYTICS
import LinkGenerator from "@lululemon/ecom-common/src/lib/link-generator";
import { createEncodedKey } from "@lululemon/ecom-common/src/util/helpers";
import { formatFilterTracking } from "@lululemon/ecom-common/src/lib/analytics";
import { log } from "@lululemon/mwa-utils";
import { setAppliedFilters } from "../../../../lib/analytics";
import { sendFilterClickSignals, getDerivedFacetItemData } from "../../utils";

// COMPONENTS
import SortAccordion from "../sort-accordion/sort-accordion";

// STYLES
import styles from "./facet-item.module.scss";

// SERP AND CDP CONTEXT
import useParseContext from "../../hooks/useParseContext";

//COLLECTION DESCRIPTION FOR AB TEST
import { collectionMockDescription } from "../../../../containers/category-page/data/cdp-collection-description";

// RENDERS THE FACETS
const renderFacets = ({
  facetType,
  refinement,
  isElementTabbable,
  isTabletOrBelow,
  collectionExplainerABFlag,
}) => {
  const {
    label,
    colorGroup,
    isSelected,
    description,
    "trademark-label": trademark,
  } = refinement;

  const shouldShowDesc = !!description && isTabletOrBelow;

  switch (facetType) {
    case "size":
      return (
        <SizeTile
          text={label}
          isSelected={!!isSelected}
          className={styles.sizeTileFilter}
          data-testid="updated-size-swatch"
        />
      );
    case "color":
      return (
        <>
          <ColorSwatch
            id={colorGroup}
            name={label}
            isSelected={!!isSelected}
            className={styles.swatchImage}
            data-testid="updated-colour-swatch"
          />
          <span className={styles.colorSwatchLabel}>{label}</span>
        </>
      );
    case "checkbox":
      return (
        <Checkbox
          required={false}
          id={label}
          name="checkbox"
          classes={{
            labelContent: "lll-text-body-2",
          }}
          checked={!!isSelected}
          tabIndex={isElementTabbable}
          onChange={() => {}}
        >
          <>
            {label}
            {trademark}
            {shouldShowDesc && (
              <span
                className={classNames("lll-text-body-3", "OneLinkTx", [
                  styles.fabricDescription,
                ])}
              >
                {description}
              </span>
            )}
            {collectionExplainerABFlag &&
              !description &&
              collectionMockDescription[label] && (
                <span
                  className={classNames("lll-text-body-3", "OneLinkTx", [
                    styles.fabricDescription,
                  ])}
                >
                  {collectionMockDescription[label]
                    ? collectionMockDescription[label]
                    : ""}
                </span>
              )}
          </>
        </Checkbox>
      );
    default:
      return label;
  }
};

const FacetItem = React.memo(
  ({
    refinement,
    enableVisualSizeStyling,
    enableVisualColorStyling,
    isMultiSelect,
    index,
    isSort,
    name: accordionTitleName,
    hasSizeDivider,
    primaryNumRefinements,
    isTrucatedFilterExpanded,
    hasNoFollow,
    shouldDisplayTruncatedFilters,
    onSortMenuSelectClick,
    dataTestId,
  }) => {
    const {
      handlePartialLoading,
      fusionQueryId,
      isTabletOrBelow,
      fusionSerp,
      isSERP,
      collectionExplainerABFlag,
    } = useParseContext();

    try {
      if (isSort) {
        return (
          <SortAccordion
            facet={refinement}
            index={index}
            onSortMenuSelectClick={onSortMenuSelectClick}
          />
        );
      }

      const {
        finalURL,
        CFSizeArialabels,
        facetType,
        facetItemName,
        isElementTabbable,
        isElementHidden,
        isDefaultHidden,
      } = getDerivedFacetItemData({
        refinement,
        enableVisualSizeStyling,
        enableVisualColorStyling,
        isMultiSelect,
        isTrucatedFilterExpanded,
        primaryNumRefinements,
        index,
        fusionSerp,
        shouldDisplayTruncatedFilters,
      });

      return (
        <>
          {hasSizeDivider && (
            <li
              className={styles.sizeDivider}
              role="presentation"
              data-testid="facet-size-divider"
            />
          )}
          <li
            key={`FacetNavExpand--${facetItemName
              .split(" ")
              .join("")}-${accordionTitleName}`}
            className={classNames(styles.facetAccordionWrapper, "OneLinkTx", {
              [styles.sizeFacets]: enableVisualSizeStyling,
              [styles.colorFacets]: enableVisualColorStyling,
              "col-xs-6": enableVisualColorStyling,
              "col-xs-12":
                !enableVisualColorStyling && !enableVisualSizeStyling,
            })}
            {...(dataTestId ? { "data-testid": dataTestId } : {})}
          >
            <LinkGenerator
              rel={isSERP || hasNoFollow ? "nofollow" : undefined}
              data-testid="facet-item"
              tabindex={isElementTabbable}
              onClick={(e) => {
                // bail out if the user is trying to open a new tab
                if (e?.metaKey || e?.ctrlKey) return;

                handlePartialLoading();
                // TODO POSSIBLY REMOVE
                sendFilterClickSignals({
                  accordionTitleName,
                  facetItemName,
                  fusionQueryId,
                });
                // Set filters selected by Guest in digitalData
                setAppliedFilters(accordionTitleName, facetItemName);
              }}
              href={finalURL}
              aria-label={CFSizeArialabels}
              analyticsAttrs={formatFilterTracking(
                facetItemName,
                index,
                isElementHidden,
                isDefaultHidden,
                accordionTitleName,
                isMultiSelect
              )}
              className={classNames({
                "lll-link-primary-impact lll-text-body-2": !isMultiSelect,
                [styles.colorSwatchFilter]: enableVisualColorStyling,
              })}
            >
              <React.Fragment key={createEncodedKey({ ...refinement, index })}>
                {renderFacets({
                  facetType,
                  refinement,
                  isElementTabbable,
                  isTabletOrBelow,
                  collectionExplainerABFlag,
                })}
              </React.Fragment>
            </LinkGenerator>
          </li>
        </>
      );
    } catch (e) {
      log.error("Failed to render FacetItem", e);
      return null;
    }
  }
);

FacetItem.displayName = "FacetItem";

FacetItem.defaultProps = {
  enableVisualSizeStyling: false,
  enableVisualColorStyling: false,
  primaryNumRefinements: 0,
  shouldDisplayTruncatedFilters: false,
};

FacetItem.propTypes = {
  shouldDisplayTruncatedFilters: PropTypes.bool,
  enableVisualColorStyling: PropTypes.bool,
  enableVisualSizeStyling: PropTypes.bool,
  hasSizeDivider: PropTypes.bool.isRequired,
  index: PropTypes.number.isRequired,
  isMultiSelect: PropTypes.bool.isRequired,
  isSort: PropTypes.bool.isRequired,
  isTrucatedFilterExpanded: PropTypes.bool.isRequired,
  name: PropTypes.string.isRequired,
  primaryNumRefinements: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.string,
  ]),
  hasNoFollow: PropTypes.bool.isRequired,
  refinement: PropTypes.shape({
    text: PropTypes.string,
    value: PropTypes.string,
  }).isRequired,
};

export default FacetItem;
